import React, { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import Authorizer from '../../../../../components/Authorizer';
import LoadingSpinner from '../../../../../components/Spinner';
import WatcherIconButton from '../../../../../components/WatcherIconButton';
import { ASSIGN_RECORD_WATCHERS } from '../../../../../constants/permissions';
import { useCompanyActiveUsers } from '../../../../../services/company-service';
import { useCreateRecordWatcher, useDeleteRecordWatcher, useRecordWatchers } from '../../../../../services/record-service';

const RecordWatcherIconButtonContainer = ({
  companyId,
  insuredId,
  recordId,
  departmentId,
  currentUser,
  stopPropagation = false,
  placement = 'bottom',
  maxHeight = '200px'
}) => {
  const { data: watchers, isLoading: watchersLoading } = useRecordWatchers(recordId);
  const { data: companyUsers, isLoading: companyUsersLoading } = useCompanyActiveUsers(companyId);
  const { mutate: createRecordWatcher } = useCreateRecordWatcher({ recordId, insuredId });
  const { mutate: deleteRecordWatcher } = useDeleteRecordWatcher({ recordId, insuredId });

  const isWatcher = useMemo(() => {
    if (watchersLoading) {
      return false;
    }

    return watchers.find(watcher => watcher.userId === currentUser.userId);
  }, [currentUser, watchers, watchersLoading]);

  if (watchersLoading) {
    return (
      <Box w="50px">
        <LoadingSpinner isInline minHeight="50px" spinnerSize="sm" />
      </Box>
    );
  }

  return (
    <Authorizer departmentId={departmentId} permission={ASSIGN_RECORD_WATCHERS}>
      {({ authorized }) => (
        <WatcherIconButton
          canManageWatchers={authorized}
          companyUsers={companyUsers}
          companyUsersLoading={companyUsersLoading}
          createWatcher={user => createRecordWatcher(user)}
          currentUser={currentUser}
          deleteWatcher={user => deleteRecordWatcher(user)}
          isWatcher={isWatcher}
          maxHeight={maxHeight}
          placement={placement}
          stopPropagation={stopPropagation}
          toolTip="Record Watchers receive email notifications as the record is updated.  Select the eye icon to start or stop watching the record."
          watchers={watchersLoading ? [] : watchers}
        />
      )}
    </Authorizer>
  );
};

RecordWatcherIconButtonContainer.propTypes = {
  companyId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  insuredId: PropTypes.number.isRequired,
  departmentId: PropTypes.number,
  currentUser: PropTypes.shape({ userId: PropTypes.number }).isRequired,
  stopPropagation: PropTypes.bool,
  placement: PropTypes.string,
  maxHeight: PropTypes.string
};

export default RecordWatcherIconButtonContainer;
